import { Cradle } from "services/serviceContainer.types";
import get from "lodash/get";
import toInteger from "lodash/toInteger";
import toString from "lodash/toString";
import { ServiceError } from "services/ServiceError";
import { IAccessRequestService } from "services/access-request/AccessRequestService.types";
import { AccessRequestStatusUtil, IAccessRequestEntity } from "models/AccessRequest.model";
import { ApplicationStatusUtil } from "models/ApplicationStatus.model";

export class AccessRequestService implements IAccessRequestService {
  private readonly apiClient: Cradle["apiClient"];

  constructor(args: Pick<Cradle, "apiClient">) {
    this.apiClient = args.apiClient;
  }

  public async fetchAccessRequests() {
    const response = await this.apiClient.protectedApi.get(`/user/access-requests-v2`);
    const json = response.data;

    const incomingAccessRequests = get(json, "incomingAccessRequests");
    const myAccessRequests = get(json, "myAccessRequests");
    if (!Array.isArray(incomingAccessRequests) || !Array.isArray(myAccessRequests)) {
      throw new ServiceError();
    }

    return {
      incomingAccessRequests: incomingAccessRequests.map(this.parseAccessRequestEntity),
      myAccessRequests: myAccessRequests.map(this.parseAccessRequestEntity),
    };
  }

  public async respondToApplicationAccessRequest(args: { requestGuid: string; response: string }): Promise<void> {
    const payload = {
      status: args.response,
    };
    await this.apiClient.protectedApi.patch(`/user/access-requests/${args.requestGuid}`, payload);
  }

  private parseAccessRequestEntity(json: any): IAccessRequestEntity {
    const requestUserJson = get(json, "requestUser");
    const applicationRequest: IAccessRequestEntity = {
      requestGuid: toString(get(json, "requestGuid")),
      applicationId: toInteger(get(json, "applicationId")),
      applicationNumber: toString(get(json, "applicationNumber")),
      consentNumber: toString(get(json, "consentNumber")),
      applicationStatus: ApplicationStatusUtil.parse(toString(get(json, "applicationStatus"))),
      status: AccessRequestStatusUtil.parse(toString(get(json, "status"))),
      address: toString(get(json, "address")),
      requestUser: {
        email: toString(get(requestUserJson, "email")),
        firstName: toString(get(requestUserJson, "firstName")),
        lastName: toString(get(requestUserJson, "lastName")),
        countryCode: toString(get(requestUserJson, "userProfile.phoneCountryCode")),
        phoneNumber: toString(get(requestUserJson, "userProfile.contactNumber")),
        organisation: toString(get(requestUserJson, "organisationName")),
        address: {
          address1: toString(get(requestUserJson, "address.address1")),
          address2: toString(get(requestUserJson, "address.address2")),
          city: toString(get(requestUserJson, "address.city")),
          state: toString(get(requestUserJson, "address.state")),
          country: toString(get(requestUserJson, "address.country")),
          zipCode: toString(get(requestUserJson, "address.zipCode")),
          isManualAddress: Boolean(get(requestUserJson, "address.isManualAddress")),
          fullAddress: toString(get(requestUserJson, "address.fullAddress")),
        },
      },
      createdDate: toString(get(json, "createdDate")),
      completionDate: toString(get(json, "completionDate")),
      expireTime: toString(get(json, "expireTime")),
    };
    return applicationRequest;
  }

  public async requestApplicationAccess(applicationId: number) {
    await this.apiClient.protectedApi.post("/user/access-requests-v2", {
      applicationId,
    });
  }
}
